import {
  InventoryTransactionType,
  ProductInventoryDoc,
  ProductInventoryHistoryDoc,
  ProductTransactionStatus,
} from '@gooduncles/gu-app-schema';
import { ColDef } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { Empty } from 'antd';
import { FC, useMemo, useState } from 'react';

import {
  formatNumberForAgCell,
  inventoryHistoryReferenceCollectionFormatter,
  linkCellStyle,
} from 'src/utils/ag-grid-util';
import { formatNumber } from 'src/utils/common';
import { datesToStringDateWhere, formatDate, getDefaultDates } from 'src/utils/date-util';

import useProductInventoryHistories from 'src/hooks/subscription/useProductInventoryHistories';
import useOrderById from 'src/hooks/swr/useOrderById';
import useSWRInventoryIssue from 'src/hooks/swr/useSWRInventoryIssue';
import useSWRProductSupplierInventoryReceipt from 'src/hooks/swr/useSWRProductSupplierInventoryReceipt';
import useSWRTransaction from 'src/hooks/swr/useSWRTransaction';
import useDateForTableOptions from 'src/hooks/useDateForTableOptions';

import AgCellProduct from 'src/atomic-components/atoms/AgCell/AgCellProduct';
import TableOptions from 'src/components/Common/TableOptions/TableOptions';

import OrderView from '../OrderView/OrderView';
import InventoryIssueView from './InventoryIssueView';
import ProductSupplierReceiptView from './ProductSupplierReceiptView';
import withTransactionData from './withTransactionData';

const defaultValue = getDefaultDates(3, 0);

const defaultColDef = {
  sortable: true,
  resizable: true,
  filter: true,
};

const autoGroupColumnDef: ColDef = {
  headerName: '시간',
  minWidth: 240,
  field: 'timestamp',
  valueFormatter: (params: any) => (params.value ? formatDate(params.value, 'L월 d일(EEEEEE) HH:mm:ss') : ''),
  cellRendererParams: {
    suppressCount: true,
  },
};

const aggFuncs = {
  signedSum: (params: any) => {
    const leaves = params.rowNode?.allLeafChildren;
    if (!leaves?.length) return 0;
    return leaves.reduce((sum: number, leaf: any) => {
      const qty = leaf.data?.changeQuantity ?? 0;
      return sum + (leaf.data?.transactionType === 'outbound' ? -qty : qty);
    }, 0);
  },
  // allLeafChildren 은 rowData 순서를 유지하고, useProductInventoryHistories 가
  // 같은 초 안의 행들을 사슬(before -> after) 순서로, 최신 건부터 정렬해 넘긴다.
  // 따라서 최초 변경 전 수량은 마지막 행, 최종 변경 후 수량은 첫 행에 있다.
  firstBeforeQuantity: (params: any) => {
    const leaves = params.rowNode?.allLeafChildren;
    if (!leaves?.length) return null;
    return leaves[leaves.length - 1].data?.beforeQuantity ?? null;
  },
  lastAfterQuantity: (params: any) => {
    const leaves = params.rowNode?.allLeafChildren;
    if (!leaves?.length) return null;
    return leaves[0].data?.afterQuantity ?? null;
  },
};

interface InventoryHistoryPanelProps {
  inventory: ProductInventoryDoc | undefined;
}

/**
 * 재고 히스토리 패널
 */
const InventoryHistoryPanel: FC<InventoryHistoryPanelProps> = ({ inventory }) => {
  const { dates, setDates, leftDate, rightDate } = useDateForTableOptions(datesToStringDateWhere, defaultValue);
  const { productInventoryHistories } = useProductInventoryHistories(inventory?._id ?? null, leftDate, rightDate);
  const [selectedTransaction, setSelectedTransaction] = useState<{
    transactionType: InventoryTransactionType;
    transactionId: string;
  }>();
  const { data: transaction } = useSWRTransaction(selectedTransaction);

  const columnDefs: ColDef<ProductInventoryHistoryDoc>[] = useMemo(
    () => [
      {
        headerName: 'Id',
        field: '_id',
        width: 80,
        hide: true,
      },
      {
        headerName: '시간',
        field: 'timestamp',
        width: 180,
        hide: true,
        sort: 'desc' as const,
        rowGroup: true,
        valueFormatter: (params: any) => (params.value ? formatDate(params.value, 'L월 d일(EEEEEE) HH:mm:ss') : ''),
      },
      {
        headerName: '참조',
        field: 'referenceCollection',
        width: 120,
        cellStyle: linkCellStyle,
        valueFormatter: inventoryHistoryReferenceCollectionFormatter,
        onCellClicked: (params: any) => {
          const data = params.data as ProductInventoryHistoryDoc;
          setSelectedTransaction({
            transactionType: data.transactionType,
            transactionId: data.transactionId,
          });
        },
      },
      {
        headerName: '총 변경량',
        field: 'changeQuantity',
        width: 120,
        valueFormatter: formatNumberForAgCell,
        type: 'numericColumn',
        aggFunc: 'signedSum',
        cellRenderer: (params: any) => {
          if (params.node?.group) {
            const formattedValue = formatNumber(Math.abs(params.value));
            let type = '';
            if (params.value > 0) type = 'inbound';
            else if (params.value < 0) type = 'outbound';
            return <span className={`stock-cell ${type}`}>{formattedValue}</span>;
          }
          const formattedValue = params.value === Infinity ? '∞' : formatNumber(params.value);
          const type: string = params.data?.transactionType;
          const status: ProductTransactionStatus = params.data?.transaction?.status;
          return <span className={`stock-cell ${type?.toLowerCase() ?? ''} ${status}`}>{formattedValue}</span>;
        },
      },
      {
        headerName: '전',
        field: 'beforeQuantity',
        width: 88,
        valueFormatter: formatNumberForAgCell,
        type: 'numericColumn',
        aggFunc: 'firstBeforeQuantity',
      },
      {
        headerName: '후',
        field: 'afterQuantity',
        width: 88,
        valueFormatter: formatNumberForAgCell,
        type: 'numericColumn',
        aggFunc: 'lastAfterQuantity',
      },
      {
        headerName: '상품',
        field: 'fullName',
        width: 100,
        cellRenderer: AgCellProduct,
        cellRendererParams: (params: any) => ({
          productId: params.data?.productId,
          field: 'fullName',
        }),
        hide: true,
      },
      {
        headerName: '취소여부',
        field: 'relatedHistoryId',
        width: 104,
        hide: true,
        valueFormatter: (params: any) => {
          if (params.value) {
            return '취소됨';
          }
          return '-';
        },
      },
    ],
    [],
  );

  if (!inventory) {
    return <Empty description='재고 정보가 없습니다.' />;
  }

  return (
    <div className='height100 flexColumn'>
      <TableOptions setDates={setDates} defaultValue={dates} />
      <div className='flexRow flex1 overflowHidden'>
        <div className='ag-theme-alpine flex1'>
          <AgGridReact
            rowData={productInventoryHistories}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            autoGroupColumnDef={autoGroupColumnDef}
            aggFuncs={aggFuncs}
            groupDefaultExpanded={0}
            suppressAggFuncInHeader
            suppressMultiSort
          />
        </div>
        <div className='flex1 overflowHidden'>
          {transaction && (
            <>
              {transaction.referenceCollection === 'inventoryIssue' && (
                <InventoryIssueViewForTransaction transaction={transaction} />
              )}
              {transaction.referenceCollection === 'productSupplier' && (
                <ProductSupplierReceiptViewForTransaction transaction={transaction} />
              )}
              {transaction.referenceCollection === 'storeIssue' && <div>{transaction.referenceId}</div>}
              {transaction.referenceCollection === 'order' && (
                <div className='borderedScrollBox'>
                  <OrderViewForTransaction transaction={transaction} />
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};
export default InventoryHistoryPanel;

const InventoryIssueViewForTransaction = withTransactionData<{ inventoryIssue: any }>(
  InventoryIssueView,
  (transaction) => {
    const { inventoryIssue } = useSWRInventoryIssue(transaction.referenceId);
    return { data: inventoryIssue ? { inventoryIssue } : null };
  },
  '재고 이슈 정보가 없습니다.',
);

const ProductSupplierReceiptViewForTransaction = withTransactionData<{ supplierReceipt: any }>(
  ProductSupplierReceiptView,
  (transaction) => {
    const { supplierInventoryReceipt } = useSWRProductSupplierInventoryReceipt(transaction.referenceId);
    return { data: supplierInventoryReceipt ? { supplierReceipt: supplierInventoryReceipt } : null };
  },
  '거래처 입고 정보가 없습니다.',
);

const OrderViewForTransaction = withTransactionData<{ order: any }>(
  OrderView,
  (transaction) => {
    const { order } = useOrderById(transaction.referenceId);
    return { data: order ? { order } : null };
  },
  '주문 정보가 없습니다.',
);
